import {FilterIcon, SearchIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, TextInput} from '@primer/react'
import {clsx} from 'clsx'
import styles from './CommentsFilter.module.css'

import type {Author, Comment, Thread} from '@github-ui/diff-lines/conversation-types'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {getFirstComment} from '../../../page-data/payloads/thread-previews'
import {fileLevelCommentKey, type LineKey} from '@github-ui/diff-lines/types'
import {useGetThread} from '../../../stores/selectors'
import {useMemo} from 'react'
import type {IssueComment} from '../../../page-data/payloads/issue-comments'
import {FeatureFlags} from '@primer/react/experimental'
import {isThreadOutdated} from '../../../helpers/thread-navigation'

/**
 * Data on each thread that is used to determine if it matches the current filter
 */
type ThreadFilterData = {
  authorLogin: string
  body: string
  createdAt: string
  id: string
  isOutdated: boolean
  isResolved: boolean
  path: string
  comments: Comment[]
}

export type AuthorFilterItem = Pick<Author, 'login' | 'avatarUrl'>

/**
 * Return a set of thread ids that match the current filter state
 */
export function filterComments(
  threadPreviews: Thread[],
  filterState: CommentsFilterState,
  getThread: ReturnType<typeof useGetThread>,
): Set<string> {
  const parsedFilterKeywords = filterState.filterText ? parseFilterKeywords(filterState.filterText) : undefined

  const matchingThreadIds = threadPreviews.filter(thread => {
    const firstComment = getFirstComment(thread)
    const filePath = thread.path ?? ''

    // Get the line key for looking up the thread in the store
    const isRightOrLeftSide = thread.subject?.endDiffSide === 'RIGHT' ? 'R' : 'L'
    const lineKey = thread.subject?.originalEndLine
      ? (`${isRightOrLeftSide}${thread.subject.originalEndLine}` as LineKey)
      : fileLevelCommentKey

    // Fetch the actual thread from the store
    const storedThread = getThread(Number(thread.id), filePath, lineKey)

    const threadFilterData: ThreadFilterData = {
      authorLogin: firstComment?.author?.login ?? '',
      body: firstComment?.body ?? '',
      createdAt: firstComment?.createdAt ?? '',
      id: thread.id,
      isResolved: storedThread?.isResolved ?? thread.isResolved ?? false,
      isOutdated: isThreadOutdated(thread),
      path: filePath,
      comments: thread.commentsData?.comments ?? [],
    }

    return filterThread(threadFilterData, filterState, parsedFilterKeywords)
  })

  return new Set([...matchingThreadIds.map(thread => thread.id ?? '')])
}

/**
 * Hook that filters thread previews and returns a set of matching thread IDs.
 */
export function useFilteredThreadIds(threadPreviews: Thread[], filterState: CommentsFilterState): Set<string> {
  const getThread = useGetThread()

  return useMemo(() => filterComments(threadPreviews, filterState, getThread), [threadPreviews, filterState, getThread])
}

function filterThread(
  threadData: ThreadFilterData,
  filterState: CommentsFilterState,
  parsedFilterKeywords?: ReturnType<typeof parseFilterKeywords>,
) {
  const {filterText, hideResolvedThreads, hideOutdatedThreads} = filterState

  if (filterText) {
    const {afterDate, reviewId, id, isResolved, remainingText} = parsedFilterKeywords ?? parseFilterKeywords(filterText)

    // When is:resolved or is:unresolved is in the filter text, use that to filter
    if (isResolved !== null) {
      if (threadData.isResolved !== isResolved) {
        return false
      }
    } else if (hideResolvedThreads && threadData.isResolved) {
      return false
    }

    if (hideOutdatedThreads && threadData.isOutdated) {
      return false
    }

    if (afterDate && !isAfterDate(threadData.createdAt, afterDate)) {
      return false
    }

    // Filter by comment/thread ID
    if (id !== null && threadData.id !== id) {
      return false
    }

    // Check if any comment in the thread has the matching review ID
    if (reviewId !== null) {
      const hasMatchingReviewId = threadData.comments?.some(comment => comment.reviewId === reviewId) ?? false
      if (!hasMatchingReviewId) {
        return false
      }
    }

    if (remainingText) {
      const filterTextLower = remainingText.toLowerCase()
      if (
        !threadData.authorLogin.toLowerCase().includes(filterTextLower) &&
        !threadData.body.toLowerCase().includes(filterTextLower) &&
        !threadData.path.toLowerCase().includes(filterTextLower)
      ) {
        return false
      }
    }
  } else {
    if (hideResolvedThreads && threadData.isResolved) {
      return false
    }
    if (hideOutdatedThreads && threadData.isOutdated) {
      return false
    }
  }

  if (filterState.selectedAuthor && threadData.authorLogin !== filterState.selectedAuthor) {
    return false
  }

  return true
}

/**
 * Parses the below keywords from filter text.
 * -  `after:YYYY-MM-DD` to filter comments created after a specific date.
 * -  `review-id:NUMBER` to filter comments by a specific review ID.
 * -  `id:VALUE` to filter comments by a specific comment or thread ID.
 * -  `is:resolved` or `is:unresolved` to filter by resolution status.
 *
 * Returns parsed keywords and any remaining text for free-text matching.
 */
export function parseFilterKeywords(filterText: string): {
  afterDate: Date | null
  reviewId: number | null
  id: string | null
  isResolved: boolean | null
  remainingText: string
} {
  const afterMatch = filterText.match(/(^|\s)after:(\S+)/i)
  let afterDate: Date | null = null
  let reviewId: number | null = null
  let id: string | null = null
  let isResolved: boolean | null = null

  const isResolvedMatch = filterText.match(/(^|\s)is:(resolved|unresolved)(?=\s|$)/i)
  if (isResolvedMatch?.[2]) {
    isResolved = isResolvedMatch[2].toLowerCase() === 'resolved'
  }
  const reviewIdMatch = filterText.match(/(^|\s)review-id:(\d+)(?=\s|$)/i)
  if (reviewIdMatch?.[2]) {
    reviewId = parseInt(reviewIdMatch[2], 10)
  }
  const idMatch = filterText.match(/(^|\s)id:(\S+)(?=\s|$)/i)
  if (idMatch?.[2]) {
    id = idMatch[2]
  }

  if (afterMatch?.[2]) {
    const parsed = new Date(afterMatch[2])
    if (!isNaN(parsed.getTime())) {
      afterDate = parsed
    }
  }

  const remainingText = filterText
    .replace(/(^|\s)after:\S*/gi, '$1')
    .replace(/(^|\s)review-id:\S*/gi, '$1')
    .replace(/(^|\s)id:\S*/gi, '$1')
    .replace(/(^|\s)is:(resolved|unresolved)/gi, '$1')
    .trim()

  return {afterDate, reviewId, id, isResolved, remainingText}
}

function isAfterDate(createdAt: string, afterDate: Date): boolean {
  const commentDate = new Date(createdAt)
  return !isNaN(commentDate.getTime()) && commentDate > afterDate
}

export function useFilterIssueCommentsIds(
  issueComments: IssueComment[],
  filterState: CommentsFilterState,
): Set<string> {
  const matchingCommentIds = useMemo(
    () =>
      issueComments.filter(comment => {
        const commentFilterData: ThreadFilterData = {
          authorLogin: comment.authorLogin,
          body: comment.body,
          createdAt: comment.createdAt,
          id: comment.id,
          isResolved: false, // issue comments don't have resolved state
          isOutdated: false, // issue comments don't have outdated state
          path: '', // issue comments don't have a file path
          comments: [], // issue comments don't have thread comments
        }
        return filterThread(commentFilterData, filterState)
      }),
    [issueComments, filterState],
  )

  return new Set([...matchingCommentIds.map(comment => comment.id)])
}

/**
 * The state values of the comments filter
 */
export type CommentsFilterState = {
  filterText: string
  hideResolvedThreads: boolean
  hideOutdatedThreads: boolean
  selectedAuthor?: string
}

const defaultFilterState: CommentsFilterState = {
  filterText: '',
  hideResolvedThreads: true,
  hideOutdatedThreads: false,
}

export function getDefaultFilterState(): CommentsFilterState {
  return {...defaultFilterState}
}

type CommentsFilterProps = {
  authorList: AuthorFilterItem[]
  className?: string
  filterState: CommentsFilterState
  onFilterStateChange: (filterState: CommentsFilterState) => void
  hideReviewThreadFilters: boolean
  iconOnly?: boolean
}

export function CommentsFilter({
  authorList,
  className,
  filterState,
  onFilterStateChange,
  hideReviewThreadFilters,
  iconOnly = false,
}: CommentsFilterProps) {
  const updateFilterText = (filterText: string) => {
    onFilterStateChange({...filterState, filterText})
  }

  const toggleResolvedFilter = () => {
    onFilterStateChange({...filterState, hideResolvedThreads: !filterState.hideResolvedThreads})
  }

  const toggleOutdatedFilter = () => {
    onFilterStateChange({...filterState, hideOutdatedThreads: !filterState.hideOutdatedThreads})
  }

  const onSelectAuthor = (authorLogin: string) => {
    if (filterState.selectedAuthor === authorLogin) {
      return onFilterStateChange({...filterState, selectedAuthor: undefined})
    }

    onFilterStateChange({...filterState, selectedAuthor: authorLogin})
  }

  const hasTextFilter = filterState.filterText.trim().length > 0
  const hasAuthorFilter = !!filterState.selectedAuthor
  const hasReviewFilter = filterState.hideResolvedThreads || filterState.hideOutdatedThreads

  // The conversation panel doesn't expose resolved/outdated toggles, so those values
  // remain at their defaults and should not trigger the indicator.
  const isFilteringActive = hasTextFilter || hasAuthorFilter || (!hideReviewThreadFilters && hasReviewFilter)

  return (
    <div className={clsx('d-flex flex-row gap-2 flex-nowrap', className)}>
      <TextInput
        block
        aria-label="Filter comments"
        leadingVisual={SearchIcon}
        placeholder="Filter comments"
        value={filterState.filterText}
        onChange={event => updateFilterText(event.target.value)}
      />
      {!hideReviewThreadFilters || authorList.length > 1 ? (
        <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
          <div className="position-relative">
            <ActionMenu>
              <ActionMenu.Anchor>
                {iconOnly ? (
                  <IconButton
                    icon={FilterIcon}
                    aria-label={
                      isFilteringActive ? 'Additional comment filters (filters applied)' : 'Additional comment filters'
                    }
                  />
                ) : (
                  <Button
                    aria-label={
                      isFilteringActive ? 'Additional comment filters (filters applied)' : 'Additional comment filters'
                    }
                    className="flex-shrink-0"
                    leadingVisual={FilterIcon}
                    trailingAction={TriangleDownIcon}
                  >
                    Filter
                  </Button>
                )}
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="small">
                <ActionList selectionVariant="single">
                  {!hideReviewThreadFilters && (
                    <>
                      <ActionList.Item
                        selected={filterState.hideResolvedThreads}
                        onSelect={() => toggleResolvedFilter()}
                      >
                        Hide resolved comments
                      </ActionList.Item>
                      <ActionList.Item
                        selected={filterState.hideOutdatedThreads}
                        onSelect={() => toggleOutdatedFilter()}
                      >
                        Hide outdated comments
                      </ActionList.Item>
                    </>
                  )}
                  {authorList.length > 1 && (
                    <>
                      {!hideReviewThreadFilters && <ActionList.Divider />}
                      <ActionList.Group>
                        <ActionList.GroupHeading>Filter by</ActionList.GroupHeading>

                        {authorList.map(author => (
                          <ActionList.Item
                            key={author.login}
                            selected={filterState.selectedAuthor === author.login}
                            onSelect={() => onSelectAuthor(author.login)}
                          >
                            <ActionList.LeadingVisual>
                              <GitHubAvatar src={author.avatarUrl} />
                            </ActionList.LeadingVisual>
                            {author.login}
                          </ActionList.Item>
                        ))}
                      </ActionList.Group>
                    </>
                  )}
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
            {isFilteringActive && <span className={styles.filterIndicator} aria-hidden="true" />}
          </div>
        </FeatureFlags>
      ) : null}
    </div>
  )
}
